(() => {
  const STORAGE_KEY = 'dijaWishlist:v1';
  const isEnglish = (document.documentElement.lang || '').toLowerCase().startsWith('en');
  const text = isEnglish
    ? {
        add: 'Add to wishlist',
        remove: 'Remove from wishlist',
        removed: 'Product removed from your wishlist',
        added: 'Product added to your wishlist',
        view: 'View product',
        removeShort: 'Remove',
        empty: 'Your wishlist is empty',
        summary: (count) => (count === 1 ? '1 saved item' : `${count} saved items`),
      }
    : {
        add: 'Ajouter aux favoris',
        remove: 'Retirer des favoris',
        removed: 'Produit retiré de vos favoris',
        added: 'Produit ajouté à vos favoris',
        view: 'Voir le produit',
        removeShort: 'Retirer',
        empty: 'Votre liste de favoris est vide',
        summary: (count) => (count === 1 ? '1 article sauvegardé' : `${count} articles sauvegardés`),
      };

  const read = () => {
    try {
      const value = JSON.parse(localStorage.getItem(STORAGE_KEY) || '[]');
      return Array.isArray(value) ? value.filter((item) => item && item.handle) : [];
    } catch (error) {
      return [];
    }
  };

  const write = (items) => {
    localStorage.setItem(STORAGE_KEY, JSON.stringify(items));
    document.dispatchEvent(new CustomEvent('dija:wishlist-change', { detail: items }));
  };

  const productFromButton = (button) => ({
    handle: button.dataset.productHandle,
    title: button.dataset.productTitle || '',
    url: button.dataset.productUrl || `/products/${button.dataset.productHandle}`,
    image: button.dataset.productImage || '',
    price: Number(button.dataset.productPrice || 0),
  });

  const updateCount = (items = read()) => {
    document.querySelectorAll('[data-dija-wishlist-count]').forEach((badge) => {
      badge.textContent = items.length;
      badge.hidden = items.length === 0;
    });
  };

  const updateButtons = (items = read()) => {
    document.querySelectorAll('[data-dija-wishlist-toggle]').forEach((button) => {
      const active = items.some((item) => item.handle === button.dataset.productHandle);
      button.classList.toggle('is-active', active);
      button.setAttribute('aria-pressed', String(active));
      const label = button.querySelector('[data-dija-wishlist-label]');
      if (label) label.textContent = active ? text.remove : text.add;
    });
  };

  let toastTimer;
  const notify = (message) => {
    let toast = document.querySelector('.dija-wishlist-toast');
    if (!toast) {
      toast = document.createElement('div');
      toast.className = 'dija-wishlist-toast';
      toast.setAttribute('role', 'status');
      toast.setAttribute('aria-live', 'polite');
      document.body.appendChild(toast);
    }
    toast.textContent = message;
    toast.classList.add('is-visible');
    window.clearTimeout(toastTimer);
    toastTimer = window.setTimeout(() => toast.classList.remove('is-visible'), 1800);
  };

  const toggle = (button) => {
    const product = productFromButton(button);
    if (!product.handle) return;
    const items = read();
    const index = items.findIndex((item) => item.handle === product.handle);
    if (index >= 0) {
      items.splice(index, 1);
      notify(text.removed);
    } else {
      items.unshift(product);
      notify(text.added);
    }
    write(items);
  };

  const money = (cents, currency) => {
    try {
      return new Intl.NumberFormat(document.documentElement.lang || 'fr-CA', {
        style: 'currency',
        currency: currency || window.Shopify?.currency?.active || 'CAD',
      }).format(Number(cents || 0) / 100);
    } catch (error) {
      return `${(Number(cents || 0) / 100).toFixed(2)} ${currency || 'CAD'}`;
    }
  };

  const element = (tag, className, text) => {
    const node = document.createElement(tag);
    if (className) node.className = className;
    if (typeof text === 'string') node.textContent = text;
    return node;
  };

  const createCard = (item, currency) => {
    const card = element('article', 'dija-wishlist-card');
    card.dataset.productHandle = item.handle;

    const imageLink = element('a', 'dija-wishlist-card__image-link');
    imageLink.href = item.url || `/products/${item.handle}`;
    const image = element('img', 'dija-wishlist-card__image');
    image.src = item.image || '';
    image.alt = item.title || '';
    image.loading = 'lazy';
    image.width = 720;
    image.height = 900;
    imageLink.appendChild(image);

    const body = element('div', 'dija-wishlist-card__body');
    const heading = element('h2', 'dija-wishlist-card__title');
    const titleLink = element('a', '', item.title || item.handle);
    titleLink.href = imageLink.href;
    heading.appendChild(titleLink);
    const price = element('p', 'dija-wishlist-card__price', money(item.price, currency));

    const actions = element('div', 'dija-wishlist-card__actions');
    const view = element('a', 'dija-wishlist-view', text.view);
    view.href = imageLink.href;
    const remove = element('button', 'dija-wishlist-remove', text.removeShort);
    remove.type = 'button';
    remove.dataset.dijaWishlistRemove = item.handle;
    actions.append(view, remove);
    body.append(heading, price, actions);
    card.append(imageLink, body);
    return card;
  };

  const renderPage = (items = read()) => {
    const root = document.querySelector('[data-dija-wishlist-page]');
    if (!root) return;
    const grid = root.querySelector('[data-dija-wishlist-grid]');
    const empty = root.querySelector('[data-dija-wishlist-empty]');
    const content = root.querySelector('[data-dija-wishlist-content]');
    const summary = root.querySelector('[data-dija-wishlist-summary]');
    const currency = root.dataset.currency || 'CAD';

    grid.replaceChildren(...items.map((item) => createCard(item, currency)));
    empty.hidden = items.length > 0;
    content.hidden = items.length === 0;
    summary.textContent = text.summary(items.length);
  };

  document.addEventListener('click', (event) => {
    const toggleButton = event.target.closest('[data-dija-wishlist-toggle]');
    if (toggleButton) {
      event.preventDefault();
      toggle(toggleButton);
      return;
    }

    const removeButton = event.target.closest('[data-dija-wishlist-remove]');
    if (removeButton) {
      const handle = removeButton.dataset.dijaWishlistRemove;
      write(read().filter((item) => item.handle !== handle));
      notify(text.removed);
      return;
    }

    const clearButton = event.target.closest('[data-dija-wishlist-clear]');
    if (clearButton) {
      write([]);
      notify(text.empty);
    }
  });

  document.addEventListener('dija:wishlist-change', (event) => {
    const items = event.detail || read();
    updateCount(items);
    updateButtons(items);
    renderPage(items);
  });

  window.addEventListener('storage', () => {
    const items = read();
    updateCount(items);
    updateButtons(items);
    renderPage(items);
  });

  const init = () => {
    const items = read();
    updateCount(items);
    updateButtons(items);
    renderPage(items);
  };

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init, { once: true });
  } else {
    init();
  }
})();
